import type {
  SolarComputeHostingPlan,
  SolarComputePayoutDto,
} from "~/api/dto/solar-proposal-compute.dto"
import type {
  SolarNetSavingsComputeDto,
  SolarPaymentBasis,
  SolarProposalNetSavingsDto,
  SolarProposalSavingsDto,
} from "~/api/dto/solar-proposal-savings.dto"

import type {
  SolarComputeSavingsView,
  SolarDialYear,
  SolarNetSavingsView,
  SolarUtilitySavings,
} from "./solar-presentation"

/** `jan` reads as "Jan". */
const monthLabel = (month: string): string =>
  `${month.charAt(0).toUpperCase()}${month.slice(1)}`

const BASIS_LABELS: Record<SolarPaymentBasis, string> = {
  cash: `after paying for the system`,
  financed: `after loan payments`,
  tpo: `after lease/PPA payments`,
}

const PAYMENT_LABELS: Record<SolarPaymentBasis, string> = {
  cash: `system payment (paid up front)`,
  financed: `loan payment`,
  tpo: `lease/PPA payment`,
}

/** A Rev 2.10 payout with every figure present, as the gateway returned it. */
export type ShownComputePayout = {
  plan: SolarComputeHostingPlan
  monthlyBase: number
  monthlyBonusCap: number
  monthlyMax: number
  termYears: number
  baseTermTotal: number
  maxTermTotal: number
}

const HOSTING_PLANS: readonly SolarComputeHostingPlan[] = [`core`, `plus`, `max`]

/**
 * The compute read's payout when the section can show it: available, a plan
 * it knows, every figure present. Else null, and no figure on the page counts
 * the node.
 */
export const toShownComputePayout = (
  payout: SolarComputePayoutDto | null | undefined,
): ShownComputePayout | null =>
  payout?.available &&
  payout.plan != null &&
  HOSTING_PLANS.includes(payout.plan) &&
  payout.monthlyBase != null &&
  payout.monthlyBonusCap != null &&
  payout.monthlyMax != null &&
  payout.termYears != null &&
  payout.baseTermTotal != null &&
  payout.maxTermTotal != null
    ? {
        plan: payout.plan,
        monthlyBase: payout.monthlyBase,
        monthlyBonusCap: payout.monthlyBonusCap,
        monthlyMax: payout.monthlyMax,
        termYears: payout.termYears,
        baseTermTotal: payout.baseTermTotal,
        maxTermTotal: payout.maxTermTotal,
      }
    : null

/** The Rev 2.10 base-only figures the savings read returns with a node. */
export type SolarComputeBaseSavings = NonNullable<
  SolarNetSavingsComputeDto[`base`]
>

/**
 * The guaranteed base's figures when the page can show them, else none. Only
 * the Rev 2.10 base is ever counted; the bonus never is. `ledgerMonthlyBase` is
 * the base the compute section shows: savings from a read that disagrees with
 * it (a node changed while the savings were cached), or with no section
 * figures at all, are none. So are figures whose years don't line up with the
 * savings years, never a line that mixes compute and bill-only years, and
 * figures that pay nothing in any year. The hero, savings and disclosure all
 * ask here, so they agree on whether savings include the base.
 */
export const shownComputeBase = (
  savings: Pick<SolarProposalSavingsDto, `years`> | null,
  net: Pick<SolarProposalNetSavingsDto, `aiCompute`> | null,
  ledgerMonthlyBase: number | null,
): SolarComputeBaseSavings | undefined => {
  const base = net?.aiCompute?.base
  return savings &&
    base &&
    ledgerMonthlyBase != null &&
    base.monthlyBase === ledgerMonthlyBase &&
    base.years.length > 0 &&
    base.years.length === savings.years.length &&
    base.years.some((year) => year.computeIncome > 0)
    ? base
    : undefined
}

/** The savings years a shown base is counted in. */
export const computeBaseYears = (
  base: Pick<SolarComputeBaseSavings, `years`>,
): number => base.years.filter((year) => year.computeIncome > 0).length

/** The base's figures, or null without a node's payout. */
const toComputeSavingsView = (
  savings: SolarProposalSavingsDto,
  net: SolarProposalNetSavingsDto,
  ledgerMonthlyBase: number | null,
): SolarComputeSavingsView | null => {
  const base = shownComputeBase(savings, net, ledgerMonthlyBase)
  if (!base) return null
  const years = savings.years.map((year, index) => ({
    year: year.year,
    cumulativeUtility: year.cumulativeUtility,
    cumulativeSolar: base.years[index]?.cumulativeWithSolarTotalWithCompute ?? 0,
  }))

  return {
    // Counted from the years themselves, so any gateway's figures label right.
    incomeYears: computeBaseYears(base),
    netSavings: base.netSavingsWithCompute,
    breakEvenYear: base.breakEvenYearWithCompute,
    totalWithSolar: years[years.length - 1]?.cumulativeSolar ?? 0,
    years,
  }
}

/** Picks the server's figures; the years line up with the savings years. */
const toNetSavingsView = (
  savings: SolarProposalSavingsDto,
  net: SolarProposalNetSavingsDto,
  ledgerMonthlyBase: number | null,
): SolarNetSavingsView => ({
  compute: toComputeSavingsView(savings, net, ledgerMonthlyBase),
  basisLabel: BASIS_LABELS[net.basis],
  paymentLabel: PAYMENT_LABELS[net.basis],
  netSavings: net.netSavings,
  breakEvenYear: net.breakEvenYear,
  totalWithSolar: net.years[net.years.length - 1]?.cumulativeWithSolarTotal ?? 0,
  years: savings.years.map((year, index) => ({
    year: year.year,
    cumulativeUtility: year.cumulativeUtility,
    cumulativeSolar:
      net.years[index]?.cumulativeWithSolarTotal ??
      year.cumulativeWithSolarUtility,
  })),
})

/** Each year's dial figures; a year's system cost comes from `net`'s same year. */
const toDialYears = (
  savings: SolarProposalSavingsDto,
  net: SolarProposalNetSavingsDto | null,
): SolarDialYear[] =>
  savings.years.map((year, index) => {
    const netYear = net?.years[index]
    return {
      year: year.year,
      utilityRatePerKwh: year.utilityRatePerKwh,
      utilityMonthly: year.utilityMonthly,
      withSolarUtilityMonthly: year.withSolarUtilityMonthly,
      cumulativeSavings: year.cumulativeSavings,
      net: netYear
        ? {
            monthlyPayment: netYear.monthlyPayment,
            monthlyNetSavings: netYear.monthlyNetSavings,
            cumulativeNetSavings: netYear.cumulativeNetSavings,
          }
        : null,
    }
  })

/**
 * Formatting only; every figure arrives from the savings read.
 * `ledgerMonthlyBase` is the compute section's base, null without one.
 */
export const toSolarUtilitySavings = (
  savings: SolarProposalSavingsDto,
  net: SolarProposalNetSavingsDto | null,
  ledgerMonthlyBase: number | null = null,
): SolarUtilitySavings => ({
  // A blank stored name reads as no name, so the copy falls back to "your utility".
  utilityName: savings.utilityName?.trim() || null,
  escalatorPct: savings.escalatorPct,
  termYears: savings.termYears,
  billToday: savings.billToday.utilityMonthly,
  totalUtility: savings.totals.utility,
  totalWithSolar: savings.totals.withSolarUtility,
  totalSavings: savings.totals.savings,
  years: savings.years.map((year) => ({
    year: year.year,
    cumulativeUtility: year.cumulativeUtility,
    cumulativeSolar: year.cumulativeWithSolarUtility,
  })),
  monthlyProduction: savings.monthlyProduction.some((month) => month.kwh > 0)
    ? savings.monthlyProduction.map((month) => ({
        month: monthLabel(month.month),
        kwh: month.kwh,
      }))
    : [],
  dialYears: toDialYears(savings, net),
  net: net && toNetSavingsView(savings, net, ledgerMonthlyBase),
})
